// Shared cookie-consent constants and helpers.
// Bumping CONSENT_VERSION forces a re-prompt for all visitors regardless of expiry.

export const CONSENT_VERSION = 2;
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months
export const CONSENT_STORAGE_KEY = "cookie_consent";
export const CONSENT_UUID_KEY = "consent_uuid";
export const CONSENT_OPEN_EVENT = "open-cookie-settings";

export interface StoredConsent {
  uuid: string;
  version: number;
  given_at: string;      // ISO
  expires_at: string;    // ISO
  analytics: boolean;
  marketing: boolean;
  external: boolean;
  event_type: "grant" | "update" | "revoke";
}

export function readStoredConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredConsent>;
    if (
      !parsed ||
      typeof parsed.uuid !== "string" ||
      typeof parsed.version !== "number" ||
      typeof parsed.expires_at !== "string"
    ) {
      return null;
    }
    return parsed as StoredConsent;
  } catch {
    return null;
  }
}

export function isConsentValid(c: StoredConsent | null): boolean {
  if (!c) return false;
  if (c.version !== CONSENT_VERSION) return false;
  if (new Date(c.expires_at).getTime() < Date.now()) return false;
  return true;
}

export function getOrCreateConsentUuid(): string {
  let id = localStorage.getItem(CONSENT_UUID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(CONSENT_UUID_KEY, id);
  }
  return id;
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

// Expose on window so external triggers (e.g. legacy markup) can call it.
if (typeof window !== "undefined") {
  (window as unknown as { openCookieSettings?: () => void }).openCookieSettings = openCookieSettings;
}
